/*
* Add Client view for authenticated users
*/

import * as _ from 'lodash';
import * as React from 'react';
import { Button, ButtonGroup, ButtonToolbar, Row, Well } from 'react-bootstrap';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { push } from 'react-router-redux';
import { compose } from 'redux';
import { connectRequest } from 'redux-query';

import * as MutateActions from '../actions/mutate';
import Auth from '../auth/auth';
import * as AppComp from '../common/app-component-base';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import * as KendoWrappers from '../common/kendoforms/componentwrappers';
import { NavigationLinks } from '../common/navigation';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';
import ApiEndpoints from '../config/apiendpoints';
import * as LK from '../config/apilookupkeys';
import { AuthConfig } from '../config/authconfiguration';
import { countriesList, statesList } from '../config/geolocations';
import * as DetailFormCommon from '../inventory/detail-form/common';
import * as ReduxQueries from '../queries';

export interface IClientAddProps extends AppComp.IAppComponentProps {
    lookups?: any;
    newClient?: any;
    lastRequestDate?: any;
    navigateTo?: any;
}

export interface IClientAddState extends AppComp.IAppComponentState {
    formChanges?: any;
}

class AddClient extends AppComp.AppComponent<IClientAddProps, IClientAddState> {
    public originalFormDataCache = {};
    public kendoFormWhiteBoxSectionProps = {};
    public lastRequestDateCache = 0;

    public constructor(props: IClientAddProps) {
        super(props, 'app.client-add.');

        this.state = {
            formChanges: {},
        };
    }

    public componentDidUpdate() {
        let resetFormData = this.getFormChangesResetRequired();

        if (resetFormData && _.size(this.state.formChanges)) {
            this.setState({ formChanges: {} });
        }
    }

    // HANDLERS
    public handleSaveButton(e: any) {
        const token = _.get(this.props, 'authsession.access_token', '');

        const changeData = _.merge(
            {},
            { newClient: this.getDefaultNewClientObject() },
            { newClient: this.state.formChanges.newClient },
        );

        this.props.newClient(changeData, token, false).then(response => {
            // We can promise-chain on the mutation action to trigger state
            // transitions when it succeeds or fails.

            if (response.status >= 200 && response.status < 300) {
                this.originalFormDataCache = {};

                // FORCE RELOAD
                const newClientId = _.get(response, 'body.clientId');
                const newClientContactId = _.get(response, 'body.contacts[0].contactId');

                let auth = new Auth();
                const lsRedirectToAddBid = Formatters.bool(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid));

                if (lsRedirectToAddBid) {
                    auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientId, newClientId);
                    auth.setLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBidNewClientContactId, newClientContactId);
                    this.props.navigateTo(NavigationLinks.orderAddBid());
                } else {
                    this.props.navigateTo(NavigationLinks.clientDetail(newClientId));
                }
            } else {
                // Error during creation...
            }
        });
    }

    public handleFormInputChange(e: any, x: any) {
        // console.log('handleForminputchange', {e: e, x: x });

        const getChangesResult = this.getFormChanges(e, x);
        // console.log('handleForminputchange getChangesResult', {getChangesResult: getChangesResult, e: e, x: x });
        if (getChangesResult.applyChanges) {
            this.setState({
                formChanges: getChangesResult.formChanges
            });
        }
    }

    // HELPERS
    public getFormChanges(e: any, x: any) {
        let result = {
            applyChanges: false,
            formChanges: {},
            formChangesResult: {},
        };

        let fieldName = '';
        let newValue;

        let formChangesCopy = _.cloneDeep(this.state.formChanges);

        if (_.get(e, 'target.props.name')) {
            // Kendo form element
            if (_.has(e, 'target.combobox')) {
                // combobox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'target.value');
            } else {
                // assume textbox
                fieldName = _.get(e, 'target.props.name', '');
                newValue = _.get(e, 'value');
            }
        } else {
            const eventTargetType = _.get(e, 'target.type', '');

            switch (eventTargetType) {
                case 'textarea':
                case 'text':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                case 'kendo-dropdownlist':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value', '');
                    break;

                case 'virtualized-select':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(x, 'value');
                    break;

                case 'delete-button':
                    fieldName = _.get(e, 'target.name', '');
                    newValue = _.get(e, 'target.value');
                    break;

                default:
                    // console.log(`*** handleFormInputChange switch hit default for eventTargetType ${eventTargetType}`);
                    break;
            }
        }

        // console.log(`handleFormInputChange continuing 1 fieldName ${fieldName} newValue ${newValue}`, { e: e, x: x });

        if (!fieldName) { return result; }

        let applyChange = false;
        let origVal = _.get(this.originalFormDataCache, fieldName);
        let formChangesResult = {};
        if (_.isArray(origVal)) {
            if (_.isArray(newValue)) {
                if (!_.isEqual(origVal, newValue)) {
                    _.set(formChangesCopy, fieldName, _.cloneDeep(newValue));
                    _.set(formChangesResult, fieldName, _.cloneDeep(newValue));
                    applyChange = true;
                } else {
                    applyChange = false;
                }
            } else {
                if (_.includes(origVal, newValue)) {
                    // console.log(`original array already contained val ${newValue} origVal`, origVal);
                    applyChange = false;
                } else {
                    const formChangesArr = _.get(formChangesCopy, fieldName, []);
                    // console.log(`${fieldName} formChangesArr`, formChangesArr);
                    if (_.includes(formChangesArr, newValue)) {
                        // console.log(`formChangesArr field ${fieldName} array already contains val ${newValue} formChangesArr`, formChangesArr);
                        applyChange = false;
                    } else {
                        formChangesArr.push(newValue);
                        _.set(formChangesCopy, fieldName, _.cloneDeep(formChangesArr));
                        _.set(formChangesResult, fieldName, _.cloneDeep(formChangesArr));
                        applyChange = true;
                        // console.log(`formChangesArr field ${fieldName} array DID NOT have val ${newValue}, applying change`, formChangesArr);
                    }
                }
            }
        } else {
            if (String(newValue) !== String(origVal)) {
                // New value
                _.set(formChangesCopy, fieldName, newValue);
                _.set(formChangesResult, fieldName, newValue);
                applyChange = true;
            } else {
                // Same as orig val
                if (_.has(formChangesCopy, fieldName)) {
                    _.unset(formChangesCopy, fieldName);
                    _.set(formChangesResult, fieldName, newValue);
                    applyChange = true;
                }
            }
        }

        if (applyChange) {
            result.applyChanges = true;
            result.formChanges = _.cloneDeep(formChangesCopy);
            result.formChangesResult = _.cloneDeep(formChangesResult);
        }

        // console.log(`getFormChanges returns result`, result);

        return result;
    }

    public buildOptions(lookups: any, key: any, srcValueField: any = 'name', srcTextField: any = 'name') {
        const l = _.get(lookups, key, []);

        if (_.size(l) <= 0) { return []; }

        return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getStr(c, srcValueField), text: Formatters.getStr(c, srcTextField) })), 'value');
    }

    public buildOptionsIntValue(lookups: any, key: any, srcValueField: any = 'name', srcTextField: any = 'name') {
        const l = _.get(lookups, key, []);

        if (_.size(l) <= 0) { return []; }

        return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getInt(c, srcValueField), text: Formatters.getStr(c, srcTextField) })), 'value');
    }

    public buildOptionsGeolocations(l: Array<any> = [], srcValueField: any = 'name', srcTextField: any = 'name') {
        if (_.size(l) <= 0) { return []; }

        return _.orderBy(l.map((c) => _.merge({}, { value: Formatters.getStr(c, srcValueField), text: Formatters.getStr(c, srcTextField) })), 'value');
    }

    public buildOptionsClientContacts(l: Array<any> = [], srcValueField: any = 'contactId') {
        if (_.size(l) <= 0) { return []; }

        const mapped = l.map((c) => {
            const contactName = Formatters.composeClientContactFormal(c);
            return _.merge({}, { value: Formatters.getInt(c, srcValueField), text: contactName });
        });

        return _.orderBy(mapped, 'text');
    }

    public getFormData(regions: any, states: any, countries: any, clientTypes: any, lookups: any) {
        const client = _.merge({}, this.state.formChanges);

        const clientRegionOptions = (_.size(regions)) ?
            regions.map((c) => _.merge({}, { value: Formatters.getStr(c, 'regionId'), text: Formatters.getStr(c, 'name') }))
            : [];

        const clientStateOptions = (_.size(states)) ?
            states.map((c) => _.merge({}, {
                value: Formatters.getStr(c, 'stateCode'),
                text: Formatters.getStr(c, 'name'),
            })) : [];

        const clientCountryOptions = (_.size(countries)) ?
            countries.map((c) => _.merge({}, {
                value: Formatters.getStr(c, 'countryCode'),
                text: Formatters.getStr(c, 'name'),
            })) : [];

        const clientTypeOptions = (_.size(clientTypes)) ?
            clientTypes.map((c) => _.merge({}, { value: Formatters.getStr(c, 'clientTypeId'), text: Formatters.getStr(c, 'name') }))
            : [];

        let newClient = _.get(client, 'newClient', this.getDefaultNewClientObject());

        let result = {
            newClient: newClient,

            clientStateOptions: (_.size(clientStateOptions)) ? clientStateOptions : [],
            clientCountryOptions: (_.size(clientCountryOptions)) ? clientCountryOptions : [],
            clientRegionOptions: (_.size(clientRegionOptions)) ? clientRegionOptions : [],
            clientTypeOptions: (_.size(clientTypeOptions)) ? clientTypeOptions : [],

            options: {
                salutationTypes: this.buildOptions(lookups, LK.LookupKeys.Salutations, 'salutation', 'salutation'),
            }
        };

        return _.merge({}, result, this.state.formChanges);
    }

    public getDefaultNewClientObject() {
        return _.cloneDeep({
            clientType: {
                clientTypeId: '',
            },
            fax: '',
            inactive: false,
            location: {
                address1: '',
                address2: '',
                city: '',
                countryCode: 'US',
                postalCode: '',
                stateCode: 'NV',
            },
            name: '',
            notes: '',
            phone: '',
            region: {
                regionId: '',
            },
            contact: {
                firstName: '',
                lastName: '',
                title: '',
                salutation: '',
                phone: '',
                email: '',
                notes: '',
            }
        });
    }

    public getFormChangesCount() {
        return _.size(_.get(this.state, 'formChanges.newClient'));
    }

    public getFormChangesResetRequired() {
        let resetFormData = false;

        // Props values
        const prevVal = _.get(this.props, 'lastRequestDate');

        // Cached values
        const cacheVal = this.lastRequestDateCache;

        // Determine if reset required
        resetFormData = (_.isEqual(prevVal, cacheVal)) ? false : true;

        // console.log(`getFormChangesResetRequired prevVal ${prevVal} cacheVal ${cacheVal} resetFormData ${resetFormData}`);

        return resetFormData;
    }

    // RENDERERS
    public renderHeaderButtonToolbar = () => {
        let saveChangesButtonProps = {
            bsStyle: 'default',
            bsSize: 'sm',
            disabled: true,
            title: this.appTextCommon('buttons.no-changes'),
        };

        if (this.getFormChangesCount()) {
            saveChangesButtonProps = {
                bsStyle: 'success',
                bsSize: 'sm',
                disabled: false,
                title: this.appTextCommon('buttons.add-document'),
            };
        }

        return (
            <div>
                <Well className="header-well-buttontoolbar">
                    <Row>
                        <Col xs={3}>
                            <ButtonToolbar>
                                <ButtonGroup>
                                    <Button {...saveChangesButtonProps} onClick={e => { this.handleSaveButton(e); }}><i className="glyphicon glyphicon-floppy-disk" /> {saveChangesButtonProps.title}</Button>
                                </ButtonGroup>
                            </ButtonToolbar>
                        </Col>
                    </Row>
                </Well>
            </div>
        );
    }

    public renderSectionNewClientForm(formData: any) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let inputPropsDropdownDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 5,
            className: 'inventory-detail-input-full-width',
        });

        let inputPropsSmallWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 3,
        });

        let inputPropsMedWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 4,
        });

        let inputPropsLargeWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 7,
        });

        const inputPropsTextAreaDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
            colLabelXs: 12,
            colInputXs: 12,
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('add-client-address')}>
                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.name"
                        label={this.appText('client-name')}
                        placeholder={this.appText('client-name.placeholder')}
                        value={Formatters.getStr(formData, 'newClient.name')}
                        required={true}
                        {...inputPropsLargeWidthTextDebounced}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.location.address1"
                        label={this.appText('address')}
                        placeholder={this.appText('address.placeholder')}
                        required={true}
                        value={Formatters.getStr(formData, 'newClient.location.address1')}
                        {...inputPropsLargeWidthTextDebounced}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.location.address2"
                        label={this.appText('address2')}
                        placeholder={this.appText('address2.placeholder')}
                        value={Formatters.getStr(formData, 'newClient.location.address2')}
                        {...inputPropsLargeWidthTextDebounced}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.location.city"
                        label={this.appText('city')}
                        placeholder={this.appText('city.placeholder')}
                        required={true}
                        value={Formatters.getStr(formData, 'newClient.location.city')}
                        {...inputPropsMedWidthTextDebounced}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newClient.location.stateCode"
                        label={this.appText('state')}
                        textField="text"
                        valueField="value"
                        required={true}
                        data={_.get(formData, 'clientStateOptions', [])}
                        value={_.get(formData, 'newClient.location.stateCode', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newClient.location.stateCode', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />
                    <KendoWrappers.KendoDropdownListInput
                        name="newClient.location.countryCode"
                        label={this.appText('country')}
                        textField="text"
                        valueField="value"
                        required={true}
                        data={_.get(formData, 'clientCountryOptions', [])}
                        value={_.get(formData, 'newClient.location.countryCode', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newClient.location.countryCode', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.location.postalCode"
                        label={this.appText('zip')}
                        required={false}
                        value={Formatters.getStr(formData, 'newClient.location.postalCode')}
                        {...inputPropsSmallWidthTextDebounced}
                    />
                </DetailFormCommon.InventoryDetailFormSection>

                <DetailFormCommon.InventoryDetailFormSectionSpacing />

                <DetailFormCommon.InventoryDetailFormSection title={this.appText('add-client-phone-numbers')}>
                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.phone"
                        label={this.appText('phone')}
                        required={true}
                        value={Formatters.getStr(formData, 'newClient.phone')}
                        {...inputPropsMedWidthTextDebounced}
                    />
                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.fax"
                        label={this.appText('fax')}
                        value={Formatters.getStr(formData, 'newClient.fax')}
                        {...inputPropsMedWidthTextDebounced}
                    />
                </DetailFormCommon.InventoryDetailFormSection>

                <DetailFormCommon.InventoryDetailFormSectionSpacing />

                <DetailFormCommon.InventoryDetailFormSection title={this.appText('add-client-details')}>
                    <KendoWrappers.KendoDropdownListInput
                        name="newClient.region.regionId"
                        label={this.appText('region')}
                        textField="text"
                        valueField="value"
                        data={_.get(formData, 'clientRegionOptions', [])}
                        value={_.get(formData, 'newClient.region.regionId', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newClient.region.regionId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />

                    <KendoWrappers.KendoDropdownListInput
                        name="newClient.clientType.clientTypeId"
                        label={this.appText('client-type')}
                        textField="text"
                        valueField="value"
                        required={true}
                        data={_.get(formData, 'clientTypeOptions', [])}
                        value={_.get(formData, 'newClient.clientType.clientTypeId', '')}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newClient.clientType.clientTypeId', type: 'kendo-dropdownlist', value: _.get(o, 'target.value') } }, o); }}
                        {...inputPropsDropdownDebounced}
                    />

                    <DetailFormCommon.InventoryDetailFormSectionSpacing />

                    <KendoWrappers.KendoTextareaDebounced
                        name="newClient.notes"
                        rows={4}
                        label={this.appText('notes')}
                        value={Formatters.getStr(formData, 'newClient.notes')}
                        {...inputPropsTextAreaDebounced}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public renderSectionNewContactForm(formData: any) {
        const defaultInputProps = { colLabelXs: 4, colInputXs: 8 };

        let inputPropsDropdownDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 5,
            className: 'inventory-detail-input-full-width',
        });

        let inputPropsSmallWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 3,
        });

        let inputPropsMedWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 4,
        });

        let inputPropsLargeWidthTextDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            colInputXs: 7,
        });

        const inputPropsTextAreaDebounced = _.merge({}, defaultInputProps, {
            handleChange: (n, v) => { this.handleFormInputChange(n, v); },
            className: 'inventory-detail-input-full-width',
            colLabelXs: 12,
            colInputXs: 12,
        });

        let inputPropsVirtualizedSelect = _.merge({}, defaultInputProps, {
            // onChange: (o) => { handleFormInputChange({target: { type: 'virtualized-select' }}, o); }        
            colInputXs: 3,
        });

        return (
            <div>
                <DetailFormCommon.InventoryDetailFormSection title={this.appText('add-client-contact')}>
                    <KendoWrappers.KendoVirtualizedSelectInput
                        name="newClient.contact.salutation"
                        label={this.appText('contact-salutation')}
                        labelKey="text"
                        valueKey="value"
                        required={true}
                        value={Formatters.getStr(formData, 'newClient.contact.salutation')}
                        options={formData.options.salutationTypes}
                        onChange={(o) => { this.handleFormInputChange({ target: { name: 'newClient.contact.salutation', type: 'virtualized-select' } }, o); }}
                        {...inputPropsVirtualizedSelect}
                    />

                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.contact.firstName"
                        label={this.appText('contact-firstname')}
                        placeholder={this.appText('contact-firstname.placeholder')}
                        value={Formatters.getStr(formData, 'newClient.contact.firstName')}
                        required={true}
                        {...inputPropsMedWidthTextDebounced}
                    />

                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.contact.lastName"
                        label={this.appText('contact-lastname')}
                        placeholder={this.appText('contact-lastname.placeholder')}
                        required={true}
                        value={Formatters.getStr(formData, 'newClient.contact.lastName')}
                        {...inputPropsMedWidthTextDebounced}
                    />

                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.contact.title"
                        label={this.appText('contact-title')}
                        placeholder={this.appText('contact-title.placeholder')}
                        required={false}
                        value={Formatters.getStr(formData, 'newClient.contact.title')}
                        {...inputPropsLargeWidthTextDebounced}
                    />

                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.contact.phone"
                        label={this.appText('contact-phone')}
                        value={Formatters.getStr(formData, 'newClient.contact.phone')}
                        {...inputPropsMedWidthTextDebounced}
                    />

                    <KendoWrappers.KendoTextInputDebounced
                        name="newClient.contact.email"
                        label={this.appText('contact-email')}
                        value={Formatters.getStr(formData, 'newClient.contact.email')}
                        {...inputPropsMedWidthTextDebounced}
                    />

                    <KendoWrappers.KendoTextareaDebounced
                        name="newClient.contact.notes"
                        rows={4}
                        label={this.appText('contact-notes')}
                        value={Formatters.getStr(formData, 'newClient.contact.notes')}
                        {...inputPropsTextAreaDebounced}
                    />
                </DetailFormCommon.InventoryDetailFormSection>
            </div>
        );
    }

    public render() {
        const { lookups, lastRequestDate, ...rest } = this.props;

        const lookupsRegions = _.get(lookups, LK.LookupKeys.Regions, []);
        const lookupsClientTypes = _.get(lookups, LK.LookupKeys.ClientTypes, []);
        const lookupsStates = _.cloneDeep(statesList);
        const lookupsCountries = _.cloneDeep(countriesList);

        let pageTitle = this.appText('page-title');
        let page = (<div />);

        let auth = new Auth();
        const lsRedirectToAddBid = Formatters.bool(auth.getLocalStorageValue(AuthConfig.LocalStorageTokens.redirectToAddBid));
        // console.log(`client/add::Render auth lsRedirectToAddBid ${lsRedirectToAddBid}`, { lsRedirectToAddBid });

        if (_.size(lookupsRegions) && _.size(lookupsClientTypes)) {
            const formData = this.getFormData(lookupsRegions, lookupsStates, lookupsCountries, lookupsClientTypes, lookups);

            // console.log('pageData', {
            //     formData: formData,
            //     lookupsRegions: lookupsRegions,
            //     lookupsClientTypes: lookupsClientTypes,
            //     lookupsStates: lookupsStates,
            //     lookupsCountries: lookupsCountries,
            //     lastRequestDateCache: this.lastRequestDateCache,
            //     lastRequestDate: lastRequestDate,
            //     numChanges: this.getFormChangesCount(),
            //     formChanges: this.state.formChanges,
            // });

            if (this.getFormChangesResetRequired() || !_.size(this.originalFormDataCache)) {
                // console.log(`render setting this.lastRequestDateCache from ${this.lastRequestDateCache} to ${lastRequestDate}`);
                this.lastRequestDateCache = lastRequestDate;
                this.originalFormDataCache = _.cloneDeep(formData);
            }

            page = (
                <div>
                    <Row>
                        <Col xs={12}>
                            <div>
                                {this.renderHeaderButtonToolbar()}
                            </div>

                            <WhiteBoxPageSection {...this.kendoFormWhiteBoxSectionProps} title={this.appText('add-client')}>
                                <Row>
                                    <Col xs={6} xl={5}>
                                        {this.renderSectionNewClientForm(formData)}
                                    </Col>
                                    <Col xs={6} xl={5}>
                                        {this.renderSectionNewContactForm(formData)}
                                    </Col>
                                </Row>
                            </WhiteBoxPageSection>

                            <div>
                                {this.renderHeaderButtonToolbar()}
                            </div>
                        </Col>
                    </Row>
                </div>
            );
        }

        return (
            <AppComp.RenderPageWithLayout branding={this.props.branding} pageName={pageTitle} {...rest}>
                {page}
            </AppComp.RenderPageWithLayout>
        );
    }
}

const getApiUrlLookups = () => ApiEndpoints.getLookupsByKeyUrl([
    LK.LookupKeys.ClientTypes,
    LK.LookupKeys.Regions,
    LK.LookupKeys.Salutations,
]);

const mapPropsToConfigs = (props) => {
    return ReduxQueries.Queries.getLookupsByKeyRequest(
        getApiUrlLookups(),
        ReduxQueries.PropsAccessToken(props)
    );
};

const mapStateToProps = (state, props) => {
    const mapped = ReduxQueries.MapQueryStateToPropsByUrl(getApiUrlLookups(), state, props, {
        lastRequestDate: new Date().getTime(),
    });

    return mapped;
};

const mapDispatchToProps = {
    newClient: MutateActions.newClientMutateAsync,
    navigateTo: (path) => push(path),
};

const AddClientContainer = compose<any>(
    connect(mapStateToProps, mapDispatchToProps),
    connectRequest(mapPropsToConfigs)
)(AddClient);

export default withRouter(AddClientContainer);



// WEBPACK FOOTER //
// ./src/client/add.tsx